/**
 * Redução de ruído espectral (spectral gating + Wiener).
 *
 * Princípios:
 *  - NUNCA destrutivo: recebe um Float32Array e devolve um novo.
 *  - Perfil de ruído estimado automaticamente a partir dos quadros mais
 *    silenciosos (ou informado pelo usuário a partir de um trecho de "silêncio").
 *  - Suavização no tempo e na frequência para evitar "musical noise".
 *  - Um teto de atenuação (floorDb) impede que o vocal fique "subaquático".
 */
import { fft, hannWindow, isPow2, nextPow2 } from './fft.js';
import { clamp } from '../utils/misc.js';

export interface NoiseReductionOptions {
  fftSize?: number;
  hopSize?: number;
  /** 0..1 — intensidade da subtração. */
  amount?: number;
  /** Atenuação máxima em dB (negativo), ex.: -30. */
  floorDb?: number;
  /** Perfil de ruído forçado (por quadro, em magnitude linear). */
  noiseProfile?: Float32Array;
  /** Quantis dos quadros mais calmos usados para estimar o ruído. */
  quietQuantile?: number;
  /** Sobremultiplicador de subtração (1..3). */
  oversubtract?: number;
}

export interface NoiseReductionResult {
  data: Float32Array;
  noiseProfile: Float32Array;
  reductionDb: number;
  frames: number;
}

/** Estima o perfil de ruído usando os quadros mais silenciosos. */
export function estimateNoiseProfile(
  input: Float32Array,
  sampleRate: number,
  opts: NoiseReductionOptions = {}
): { profile: Float32Array; fftSize: number; hopSize: number } {
  const fftSize = normalizeFft(opts.fftSize ?? 2048);
  const hopSize = opts.hopSize ?? fftSize / 4;
  const win = hannWindow(fftSize);
  const bins = fftSize / 2 + 1;
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const frameEnergies: number[] = [];
  const frameMags: Float32Array[] = [];
  const frames = Math.max(1, Math.floor((input.length - fftSize) / hopSize) + 1);

  for (let f = 0; f < frames; f++) {
    const off = f * hopSize;
    re.fill(0); im.fill(0);
    let e = 0;
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      const v = s < input.length ? input[s] * win[i] : 0;
      re[i] = v;
      e += v * v;
    }
    fft(re, im, false);
    const m = new Float32Array(bins);
    for (let k = 0; k < bins; k++) m[k] = Math.hypot(re[k], im[k]);
    frameMags.push(m);
    frameEnergies.push(e);
  }

  const order = frameEnergies
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e - b.e);
  const q = clamp(opts.quietQuantile ?? 0.15, 0.02, 0.5);
  const count = Math.max(1, Math.round(order.length * q));
  const profile = new Float32Array(bins);
  for (let n = 0; n < count; n++) {
    const m = frameMags[order[n].i];
    for (let k = 0; k < bins; k++) profile[k] += m[k];
  }
  for (let k = 0; k < bins; k++) profile[k] /= count;
  return { profile, fftSize, hopSize };
}

function normalizeFft(n: number): number {
  const p = nextPow2(Math.max(256, n));
  return isPow2(n) ? n : p;
}

export function reduceNoise(
  input: Float32Array,
  sampleRate: number,
  opts: NoiseReductionOptions = {}
): NoiseReductionResult {
  const amount = clamp(opts.amount ?? 0.6, 0, 1);
  const floorDb = clamp(opts.floorDb ?? -24, -80, 0);
  const floorGain = Math.pow(10, floorDb / 20);
  const over = clamp(opts.oversubtract ?? 1 + amount * 1.6, 1, 4);

  const est = opts.noiseProfile
    ? null
    : estimateNoiseProfile(input, sampleRate, opts);
  const fftSize = est ? normalizeFft(opts.fftSize ?? 2048) : est!.fftSize;
  const hopSize = est ? (opts.hopSize ?? fftSize / 4) : est!.hopSize;
  const bins = fftSize / 2 + 1;
  const noise = opts.noiseProfile ?? est!.profile;

  const win = hannWindow(fftSize);
  // Janela de síntese para OLA sem alterar o ganho total.
  const syn = new Float32Array(fftSize);
  for (let i = 0; i < fftSize; i++) syn[i] = win[i];

  const out = new Float32Array(input.length);
  const norm = new Float32Array(input.length);
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const prevGain = new Float32Array(bins).fill(1);
  const frames = Math.max(1, Math.floor((input.length - fftSize) / hopSize) + 1);
  const smoothing = 0.55; // suavização temporal do ganho

  for (let f = 0; f < frames; f++) {
    const off = f * hopSize;
    re.fill(0); im.fill(0);
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      re[i] = s < input.length ? input[s] * win[i] : 0;
    }
    fft(re, im, false);

    for (let k = 0; k < bins; k++) {
      const mag = Math.hypot(re[k], im[k]);
      const nz = noise[k] * over + 1e-9;
      // Wiener: G = max(floor, (|X| - a|N|) / |X|)
      let g = (mag - amount * nz * 1.0) / (mag + 1e-9);
      g = clamp(g, floorGain, 1);
      // Suaviza no tempo (evita respiração metálica)
      g = smoothing * prevGain[k] + (1 - smoothing) * g;
      prevGain[k] = g;
      re[k] *= g;
      im[k] *= g;
    }

    fft(re, im, true);
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      if (s >= out.length) break;
      out[s] += re[i] * syn[i];
      norm[s] += syn[i] * win[i];
    }
  }

  for (let i = 0; i < out.length; i++) {
    if (norm[i] > 1e-6) out[i] /= norm[i];
  }
  return {
    data: out,
    noiseProfile: noise,
    reductionDb: Math.abs(floorDb) * amount,
    frames,
  };
}

/**
 * De-esser "offline" opcional: atenua a banda de sibilância nos quadros em que
 * ela domina. Usado apenas na exportação quando o de-esser em tempo real
 * não for suficiente. Não destrutivo.
 */
export function reduceSibilance(
  input: Float32Array,
  sampleRate: number,
  strength: 'suave' | 'medio' | 'forte' = 'medio'
): Float32Array {
  const cfg = { suave: 0.35, medio: 0.55, forte: 0.75 }[strength];
  const fftSize = 2048;
  const hopSize = fftSize / 4;
  const lo = Math.round((5000 / (sampleRate / 2)) * (fftSize / 2));
  const hi = Math.round((9500 / (sampleRate / 2)) * (fftSize / 2));
  const { profile } = estimateNoiseProfile(input, sampleRate, { fftSize, hopSize });
  const res = reduceNoise(input, sampleRate, {
    fftSize,
    hopSize,
    amount: cfg,
    floorDb: -12,
    noiseProfile: profile,
  });
  // Mistura: só a banda sibilante é substituída pela versão reduzida.
  const out = new Float32Array(input.length);
  const win = hannWindow(fftSize);
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const re2 = new Float32Array(fftSize);
  const im2 = new Float32Array(fftSize);
  const frames = Math.max(1, Math.floor((input.length - fftSize) / hopSize) + 1);
  const norm = new Float32Array(input.length);
  for (let f = 0; f < frames; f++) {
    const off = f * hopSize;
    re.fill(0); im.fill(0); re2.fill(0); im2.fill(0);
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      re[i] = (s < input.length ? input[s] : 0) * win[i];
      re2[i] = (s < res.data.length ? res.data[s] : 0) * win[i];
    }
    fft(re, im, false);
    fft(re2, im2, false);
    for (let k = lo; k <= hi && k < fftSize / 2 + 1; k++) {
      re[k] = re2[k];
      im[k] = im2[k];
    }
    fft(re, im, true);
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      if (s >= out.length) break;
      out[s] += re[i] * win[i];
      norm[s] += win[i] * win[i];
    }
  }
  for (let i = 0; i < out.length; i++) if (norm[i] > 1e-6) out[i] /= norm[i];
  return out;
}
